import { RouterStateSerializer } from '@ngrx/router-store';
import { RouterStateSnapshot, Params } from '@angular/router';
import { Pipe, PipeTransform } from '@angular/core';
import * as dayjs from 'dayjs';
//import dayjs from 'dayjs';


/**
 * The RouterStateSerializer takes the current RouterStateSnapshot
 * and returns any pertinent information needed. The snapshot contains
 * all information about the state of the router at the given point in time.
 * The entire snapshot is complex and not always needed. In this case, you only
 * need the URL and query parameters from the snapshot in the store. Other items could be
 * returned such as route parameters and static route data.
 */

export interface RouterStateUrl {
	url: string;
	params: Params;
	queryParams: Params;
}

export class CustomRouterStateSerializer implements RouterStateSerializer<RouterStateUrl> {
	serialize(routerState: RouterStateSnapshot): RouterStateUrl {
		let route = routerState.root;

		while (route.firstChild) {
			route = route.firstChild;
		}

		const {
			url,
			root: { queryParams },
		} = routerState;
		const { params } = route;

		// Only return an object including the URL, params and query params
		// instead of the entire snapshot
		return { url, params, queryParams };
	}
}

export function orderByProperty(arr: any[], prop: string, ascending?: boolean) {
	const newArray = [...arr]
	return newArray
		.sort(function (a, b) {
				// always send null or empty strings to the bottom of the list
				if (a[prop] === null || a[prop] === '') {
					return 1;
				} else if (b[prop] === null || b[prop] === '') {
					return -1;
				}

				let aProp: any, bProp: any;
				if (typeof a[prop] === 'string') {
					aProp = a[prop].toLowerCase().trim();
				} else {
					aProp = a[prop];
				}
				if (typeof b[prop] === 'string') {
					bProp = b[prop].toLowerCase().trim();
				} else {
					bProp = b[prop];
				}

				if (!ascending) {
					if (aProp < bProp) {
						return -1;
					}
					if (aProp > bProp) {
						return 1;
					}
				} else {
					if (aProp < bProp) {
						return 1;
					}
					if (aProp > bProp) {
						return -1;
					}
				}
				return 0; // default return value (no sorting)
		  })
}

export function orderReverseByProperty(arr: any[], prop: string) {
	return arr.sort(function (a, b) {
		const aProp = a[prop].toLowerCase(),
			bProp = b[prop].toLowerCase();
		if (aProp > bProp) {
			return -1;
		}
		if (aProp < bProp) {
			return 1;
		}
		return 0; // default return value (no sorting)
	});
}

export function orderObjectsByDateProp(objects: any[], orderValue: string) {
	return objects.sort(function compare(a, b) {
		const dateA = dayjs(a[orderValue]);
		const dateB = dayjs(b[orderValue]);
		if ( dateA.isBefore(dateB)) {
			return -1;
		} else if (dateA.isAfter(dateB)) {
			return 1;
		} else {
			return 0;
		}
	});
}

export function capitalizeString(text: string) {
	return (text = text
		.split(' ')
		.map(s => s.charAt(0).toUpperCase() + s.substring(1))
		.join(' '));
}

export function merge(obj, src) {
	Object.keys(src).forEach(function (key) {
		obj[key] = src[key];
	});
	return obj;
}

export function removeDuplicatesFromArray(arr: any[]) {
	if (!arr) {
		return null;
	}
	return arr.filter((item, index, array) => array.indexOf(item) === index);
}

export function getScrollBarWidth() {
	var inner = document.createElement('p');
	inner.style.width = '100%';
	inner.style.height = '200px';

	var outer = document.createElement('div');
	outer.style.position = 'absolute';
	outer.style.top = '0px';
	outer.style.left = '0px';
	outer.style.visibility = 'hidden';
	outer.style.width = '200px';
	outer.style.height = '150px';
	outer.style.overflow = 'hidden';
	outer.appendChild(inner);

	document.body.appendChild(outer);
	var w1 = inner.offsetWidth;
	outer.style.overflow = 'scroll';
	var w2 = inner.offsetWidth;

	if (w1 == w2) {
		w2 = outer.clientWidth;
	}

	document.body.removeChild(outer);

	return w1 - w2;
}

/**
  * @name: clearClassFromElements
  * @description: given an array of elements and a specific css class name, this function will remove the
  given class from each element.
  * @param {array} elements
  * @param {string} className
  * @returns: elements
  */
export function clearClassFromElements(elements, className) {
	for (let i = 0, len = elements.length; i < len; i++) {
		elements[i].classList.remove(className);
	}
	return elements;
}

/**
 * Uses canvas.measureText to compute and return the width of the given text of given font in pixels.
 *
 * @param {String} text The text to be rendered.
 * @param {String} font The css font descriptor that text is to be rendered with (e.g. "bold 14px verdana").
 *
 * @see https://stackoverflow.com/questions/118241/calculate-text-width-with-javascript/21015393#21015393
 */
export function getTextWidth(text, font) {
	// re-use canvas object for better performance
	const canvas = document.createElement('canvas');
	const context = canvas.getContext('2d');
	context.font = font;
	const metrics = context.measureText(text);
	return metrics.width;
}

@Pipe({
	name: 'sort',
})
export class SortPipe implements PipeTransform {
	transform(array: any, field: string): any[] {
		
		if (!Array.isArray(array)) {
			return;
		}
		const arrayCopy =[...array];
		arrayCopy.sort((a: any, b: any) => {
			if (a[field] < b[field]) {
				return -1;
			} else if (a[field] > b[field]) {
				return 1;
			} else {
				return 0;
			}
		});
		return arrayCopy;
	}
}

@Pipe({
	name: 'filterUsers',
})
export class UserFilterPipe implements PipeTransform {
	uncheckedProps = ['isAdmin', 'id', 'siteId', 'authorEmail', 'editorEmail', 'profile', 'mapKitToken', 'siteIds', 'sites'];
	transform(list: any, searchValue: string): any[] {
		if (!list) {
			return [];
		}
		if (!searchValue) {
			return list;
		}
		searchValue = searchValue.toLowerCase();

		return list.filter((it: any) => {
			let contains = false;
			for (const prop in it) {
				if (it.hasOwnProperty(prop)) {
					if (contains) {
						return contains;
					}
					if (prop === 'roles') {
						it[prop].forEach(role => {
							contains = role.name.toLowerCase().includes(searchValue);
						});
					} else if (!this.uncheckedProps.includes(prop)) {
						contains = it[prop].toLowerCase().includes(searchValue);
					}
				}
			}
			return contains;
		});
	}
}

@Pipe({
	name: 'filterRoles',
})
export class RoleFilterPipe implements PipeTransform {
	transform(list: any, searchValue: string): any[] {
		if (!list) {
			return [];
		}
		if (!searchValue) {
			return list;
		}
		searchValue = searchValue.toLowerCase();

		return list.filter((it: any) => {
			let contains = false;
			for (const prop in it) {
				if (it.hasOwnProperty(prop)) {
					if (contains) {
						return contains;
					}
					if (prop === 'name') {
						contains = it[prop].toLowerCase().includes(searchValue);
					}
				}
			}
			return contains;
		});
	}
}

@Pipe({
	name: 'filterItems',
})
export class ItemFilterPipe implements PipeTransform {
	transform(list: any, props: string[], searchValue: string): any[] {
		if (!list) {
			return [];
		}
		if (!searchValue || !props || !props.length) {
			return list;
		}
		searchValue = searchValue.toLowerCase();

		return list.filter((it: any) => {
			let contains = false;
			for (const prop in it) {
				if (it.hasOwnProperty(prop)) {
					if (contains) {
						return contains;
					}
					if (props.some(p => p === prop)) {
						contains = it[prop].toLowerCase().includes(searchValue);
					}
				}
			}
			return contains;
		});
	}
}

@Pipe({
	name: 'filterCapabilities',
})
export class CapabilityFilterPipe implements PipeTransform {
	transform(capabilityGroups: any, searchValue: string): any[] {
		if (!capabilityGroups) {
			return [];
		}
		if (!searchValue) {
			return capabilityGroups;
		}
		searchValue = searchValue.toLowerCase();

		return capabilityGroups
			.map(capabilityGroup => {
				return capabilityGroup.filter((capability: any) => {
					let contains = false;
					for (const prop in capability) {
						if (capability.hasOwnProperty(prop)) {
							if (contains) {
								return contains;
							}
							if (prop === 'name') {
								contains = capability[prop].toLowerCase().includes(searchValue);
							}
						}
					}
					return contains;
				});
			})
			.filter(capabilityGroup => capabilityGroup.length > 0);
	}
}

export function metersToFeet(meterValue: number) {
	return meterValue ? roundToQuarter(meterValue * 3.28) : null;
}

export function metersToInches(meterValue: number) {
	return meterValue ? roundToQuarter(meterValue / 0.0254) : null;
}

export function celciusToFarenheit(celciusValue: number) {
	return celciusValue !== null ? (celciusValue * 9.0) / 5.0 + 32 : null;
}

export function feetToMeters(feetValue: number) {
	return feetValue ? feetValue / 3.28 : null;
}

export function inchesToMeters(feetValue: number) {
	return feetValue ? feetValue * 0.0254 : null;
}

export function farenheitToCelcius(fValue: number) {
	return fValue !== null ? ((fValue - 32) * 5.0) / 9.0 : null;
}

//round number to the nearest 0.05 increment
export function roundToQuarter(num) {
	return Math.round(num * 20) / 20;
}

export function copy(item) {
	let result = null;
	if (!item) {
		return result;
	}
	if (Array.isArray(item)) {
		result = [];
		item.forEach(element => {
			``;
			result.push(copy(element));
		});
	} else if (item instanceof Object && !(item instanceof Function)) {
		result = {};
		for (const key in item) {
			if (key) {
				result[key] = copy(item[key]);
			}
		}
	}
	return result || item;
}

/**
 * The compression result we are expecting comes from our ngx-image-compress data uri result
 *
 * Documentation: https://www.npmjs.com/package/ngx-image-compress
 * Documentation: https://github.com/vijayrchoudhary/compressImageAngular
 */
export function fileToFormData(file: File, compressionResult): FormData {
	const fd = new FormData();
	const byteString = window.atob(compressionResult);
	const arrayBuffer = new ArrayBuffer(byteString.length);
	const int8Array = new Uint8Array(arrayBuffer);
	for (let i = 0; i < byteString.length; i++) {
		int8Array[i] = byteString.charCodeAt(i);
	}
	// File() constructor not available in IE11, so this mocks the required properties by extending the Blob type
	const imageFile: any = new Blob([int8Array], { type: file.type });
	imageFile.lastModifiedDate = file.lastModified;
	fd.append('file', imageFile, file.name);
	return fd;
}

/**
 * Retrieves the facility hierarchy and stores is on a controller variable
 * @param item: item as an object in the array
 * @param tree: tree as an array
 */
export function findLevelOfElementInTree(item, tree): number {
	if (!item) {
		return 0;
	}
	for (var i = 0; i < tree.length; i++) {
		if (
			tree[i].some(function (e) {
				return e.id == item.id;
			})
		) {
			return i;
		}
	}
	return -1;
}

export function findObjInTree(itemId, tree, childrenKey) {
	if (!itemId || !tree) {
		return null;
	}

	let selectedObj = null;
	selectedObj = tree.find(obj => obj.id === itemId);

	if (selectedObj) {
		return selectedObj;
	} else {
		for (var i = 0; i < tree.length; i++) {
			if (tree[i][childrenKey] && tree[i][childrenKey].length > 0) {
				selectedObj = findObjInTree(itemId, tree[i][childrenKey], childrenKey);
				if (selectedObj) {
					return selectedObj;
				}
			}
		}
	}

	return false;
}

/**
 * @name: findPathThroughTreeForNode
 * @description: given an array, a key, and a value this function recursively recreates a tree based on the value for
 *               a single node in a tree. This is very important when trying to re-create things like the facility
 *               hierarchy path when setting up the defaults for a form or profile.
 * @param: arr as array
 * @param: key as string
 * @param: objId as string
 * @param: currentLevelIndex as integer (just used during recursion)
 * @param: levelsCurrentlySearching as an array (just used during recursion)
 * @param: parent as object (just used during recursion)
 * @returns: an hierarchy array
 */
export function findPathThroughTreeForNode(
	arr: any[],
	key: string,
	objId: string,
	childrenKey: string,
	currentLevelIndex?,
	levelsCurrentlySearching?,
	parent?
) {
	// current level in the array tree
	currentLevelIndex !== undefined ? currentLevelIndex++ : (currentLevelIndex = 0);
	//
	levelsCurrentlySearching !== undefined
		? (levelsCurrentlySearching[currentLevelIndex] = { parent: parent, vals: arr })
		: (levelsCurrentlySearching = [{ vals: arr }]);
	levelsCurrentlySearching.splice(currentLevelIndex + 1);

	var found = false;
	// Search through the current level for the value and return true if found
	found = arr.some(function (obj) {
		return obj[key] === objId;
	});

	// if found return the current tree array
	if (found) {
		return levelsCurrentlySearching;
	} // if the element is not found in the current level
	else {
		// Search through all of the level for more levels to search through
		for (var i = 0; i < arr.length; i++) {
			// Our over all currentlevel should not be effected by this search since we will iterate over it multiple times
			var levelIndex = JSON.parse(JSON.stringify(currentLevelIndex));
			if (arr[i][childrenKey] && arr[i][childrenKey].length > 0) {
				if (findPathThroughTreeForNode(arr[i][childrenKey], key, objId, childrenKey, levelIndex, levelsCurrentlySearching, arr[i])) {
					return levelsCurrentlySearching;
				}
			}
		}
	}

	return false;
}

/**
 * Checks to see if the given string contains letters or not. This validator only
 * allows ' - ', ' . ', and numbers.
 * @param value string value to be validated against
 */
export function containsLetters(value: string): boolean {
	const expression = new RegExp(/^-?[0-9]\d*(\.\d+)?$/);
	return !expression.test(value);
}
